import type { DragEvent, FC } from "react";
import { useCallback, useRef, useState } from "react";
import { Box, FormControl, FormLabel, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";

interface Props {
  readonly file: File | null;
  readonly onFileSelected: (file: File | null) => void;
  readonly label?: string;
  readonly fullHeight?: boolean;
}

const FileDropZone: FC<Props> = ({ file, onFileSelected, label, fullHeight }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        onFileSelected(droppedFile);
      }
    },
    [onFileSelected],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  return (
    <FormControl fullWidth sx={fullHeight ? { height: "100%" } : undefined}>
      {label && <FormLabel sx={{ mb: 0.5, fontSize: "0.75rem" }}>{label}</FormLabel>}
      <Box
        data-testid="file-drop-zone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: 1,
          borderColor: isDragOver ? "primary.main" : "rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          backgroundColor: isDragOver ? "action.hover" : "transparent",
          transition: "border-color 0.2s, background-color 0.2s",
          "&:hover": {
            borderColor: "text.primary",
          },
          ...(fullHeight ? { flex: 1, minHeight: 0 } : { p: 3 }),
        }}
      >
        <AttachFileIcon sx={{ color: "action.active", mb: 0.5 }} />
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textAlign: "center",
          }}
        >
          {file ? file.name : "Przeciągnij plik lub kliknij, aby wybrać"}
        </Typography>
        {file && (
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: "pointer", mt: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              onFileSelected(null);
            }}
            data-testid="remove-file"
          >
            Usuń plik
          </Typography>
        )}
        <input
          ref={inputRef}
          type="file"
          data-testid="file-input"
          hidden
          onChange={(e) => {
            const selected = e.target.files?.[0];
            onFileSelected(selected ?? null);
          }}
        />
      </Box>
    </FormControl>
  );
};

export default FileDropZone;
